<script>
	import { browser } from '$app/environment';
	import ProductCardNew from '$components/ProductCardNew.svelte';
	import { auth } from '$lib/auth';
	import {
		priceLookup,
		lookupPointsMapping,
		reversePriceLookup
	} from '$lib/firestore/planPointsMapping.js';
	import { isAppUser } from '$stores/isAppUser';
	import { prizes, subOnlyPrizes } from '$stores/prizes';
	import { loadStripe } from '@stripe/stripe-js';
	import posthog from 'posthog-js';
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';

	let subscription = null;
	let invoices = [];
	let isLoading = true;
	let error = '';
	let subStatus = '';
	let paused = false;
	let loggedIn = false;
	let userAuth = null;
	let subscriptionFetched = false;
	let isNativeApp = false;

	let couponCode = '';
	let showCouponInput = false;

	let activeSubCount;

	let subExclusivePrizes = [];

	const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

	// Prepare the plans data with cost and saving
	const plans = Object.keys(priceLookup).map((key) => {
		const priceId = priceLookup[key];
		const reverseInfo = reversePriceLookup[priceId] || {};

		return {
			key,
			name: formatPlanName(key),
			priceId: priceId,
			points: lookupPointsMapping[key],
			features: getPlanFeatures(key),
			cost: reverseInfo.cost || 'N/A',
			saving: reverseInfo.saving || 'N/A',
			extra: reverseInfo.extra || 'N/A'
		};
	});

	// Format plan name for display
	function formatPlanName(key) {
		const nameMap = {
			test_daily: 'Test Daily',
			standard_plan_three: 'Basic',
			standard_monthly: 'Standard',
			premium_monthly: 'Premium',
			gold_monthly: 'Top Dog'
		};
		return nameMap[key] || key;
	}

	// Define plan features
	function getPlanFeatures(key) {
		const featuresMap = {
			test_daily: [
				'£1 a day plan',
				'Instantly added to your account',
				'Points auto-added each day',
				'Exclusive discounts',
				'Cancel anytime'
			],
			standard_plan_three: [
				'Instantly added to your account',
				'Points auto-added each month',
				'Exclusive discounts',
				'Cancel anytime'
			],
			standard_monthly: [
				'Instantly added to your account',
				'Points auto-added each month',
				'Exclusive discounts',
				'Cancel anytime'
			],
			premium_monthly: [
				'Instantly added to your account',
				'Points auto-added each month',
				'Exclusive discounts',
				'Cancel anytime'
			],
			gold_monthly: [
				"Automatic entry to win back everything you've spent each month — in points!",
				'Instantly added to your account',
				'Points auto-added each month',
				'Exclusive discounts',
				'Hand written Christmas card from Jamie',
				'Cancel anytime'
			]
		};
		return featuresMap[key] || [];
	}

	// Initiate the subscription process
	async function subscribe(priceId) {
		try {
			posthog.capture('cta_subscribe_clicked', {
				priceId: priceId
			});
			
			if (!userAuth) {
				throw new Error('User not authenticated.');
			}

			const { email, uid } = userAuth;

			const response = await fetch('/api/stripe/create-checkout-session', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					priceId,
					email,
					uid,
					couponCode
				})
			});

			const session = await response.json();

			if (session.error) {
				console.error('Error creating checkout session:', session.error);
				return;
			}

			posthog.capture('user_subscribed', {
				priceId: priceId,
				email: email,
				uid: uid
			});

			const stripe = await stripePromise;
			await stripe.redirectToCheckout({ sessionId: session.id });
		} catch (error) {
			console.error('Error initiating subscription:', error);
		}
	}

	// Cancel the current subscription
	async function cancelSubscription() {
		try {
			if (!userAuth) {
				throw new Error('User not authenticated.');
			}

			const { email, uid } = userAuth;
			const idToken = await userAuth.getIdToken();

			const response = await fetch('/api/stripe/cancel-subscription', {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${idToken}`,
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ email, uid })
			});

			const result = await response.json();
			if (result.error) {
				console.error('Error canceling subscription:', result.error);
				throw new Error(result.error);
			}

			console.log('Subscription canceled successfully');
		} catch (error) {
			console.error('Error canceling subscription:', error);
		}
	}

	// Handle changing the plan
	async function changePlan(newPriceId) {
		// Cancel current subscription first
		if (subscription?.status === 'active') {
			await cancelSubscription();
		}
		// Then subscribe to the new plan
		await subscribe(newPriceId);
	}

	// Fetch the total active subscribers
	async function fetchActiveSubscribers() {
		try {
			const response = await fetch('/api/stripe/get-active-subscriber-count', {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json'
				}
			});

			const result = await response.json();
			if (result.error) {
				console.error('Error fetching active subscribers:', result.error);
				throw new Error(result.error);
			}

			console.log('Active subscribers:', result.activeSubscriberCount);
			activeSubCount = result.activeSubscriberCount;
			return result.activeSubscriberCount;
		} catch (error) {
			console.error('Error fetching active subscribers:', error);
		}
	}

	// Fetch the user's subscription details
	async function fetchSubscription() {
		try {
			if (!userAuth) {
				error = 'User not authenticated.';
				throw new Error('User not authenticated.');
			}

			const idToken = await userAuth.getIdToken();

			const response = await fetch('/api/stripe/get-subscription', {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${idToken}`,
					'Content-Type': 'application/json'
				}
			});

			const result = await response.json();

			if (response.ok) {
				const subscriptionsArr = result.data.subscriptions;
				subscriptionsArr.find((thisSub) => {
					if (thisSub.status === 'active') {
						subscription = thisSub;
						subStatus = 'active';
						paused = Boolean(thisSub.overview?.pause_collection);
					}
				});
				invoices = result.data.invoices;
				subStatus = subStatus || 'cancelled';
				paused = paused || false;
			} else {
				error = result?.error || 'Failed to fetch subscription details.';
			}
		} catch (err) {
			error = err.message || 'An error occurred while fetching subscription details.';
			console.error('Subscription Fetch Error:', err);
		} finally {
			isLoading = false;
		}
	}

	onMount(() => {
		if (!browser) return;

		const unsubscribe = auth.subscribe(async (value) => {
			userAuth = value.user;
			loggedIn = !!userAuth;

			if (loggedIn && !subscriptionFetched) {
				await fetchSubscription();
				await fetchActiveSubscribers();
				subscriptionFetched = true;
			}

			isLoading = false;
		});

		subOnlyPrizes.subscribe((value) => {
			subExclusivePrizes = value;
		});

		return () => {
			unsubscribe();
		};
	});
</script>

{#if isLoading}
	<p>Loading...</p>
{:else if error}
	<div class="container">
		<div class="error-message">
			<p>{error}</p>
		</div>
	</div>
{:else if $isAppUser}
	<div class="container">
		<h1>HotDraw UK Subscriptions</h1>
		<div class="external-link">
			<p>These are unavailble on the app.</p>
		</div>
	</div>
{:else}
	<div class="container">
		<h1>Choose a Subscription Plan</h1>

		<div class="flex flex-row sub-intro">
			<div>
				<p>
					Choose from our monthly subscription options to receive site credit via points. Every
					point is valued at £1 to spend on site. <strong
						>Site credit (points) roll over and accumulate each month.</strong
					>
				</p>

				<p><strong>Getting more bang for your buck.</strong></p>
			</div>

			{#if subExclusivePrizes.length > 0}
				<div class="sub-prize-hero active-sub {subStatus === 'active' ? 'active-sub' : ''}">
					<p><strong>Exclusive Subscriber Prizes</strong></p>
					{#each subExclusivePrizes as prize}
						<div class="card">
							<div class="flex">
								<h5>{prize.name}</h5>
								{#if subStatus === 'active'}
									<p><strong>You're In!</strong></p>
								{:else}
									<p>Subscribe to Unlock</p>
								{/if}
							</div>
						</div>
					{/each}
				</div>
			{/if}
		</div>
		<br />

		<div class="plans">
			{#each plans as plan}
				<!-- Exclude a certain price if needed -->
				{#if plan.priceId !== 'price_1QuNLaLwVFtniydbR88sazWp'}
					<div class="plan">
						<h2>{plan.name}</h2>
						<p class="points-per"><strong>{plan.points} Points</strong></p>
						<div class="plan-details">
							<p><strong>{plan.extra}</strong></p>
						</div>
						<div class="features">
							<h3>Benefits:</h3>
							{#if plan.features.length > 0}
								<ul>
									{#each plan.features as feature}
										<li>{feature}</li>
									{/each}
								</ul>
							{/if}
						</div>

						{#if loggedIn}
							{#if subStatus === 'active'}
								<!-- Show 'Manage' if on the same plan, else 'Change Plan' -->
								{#if subscription?.plan_name === plan.priceId}
									<button
										class="btn manage-button"
										on:click={() => (window.location.href = '/subscribe/manage')}
									>
										Manage
									</button>
								{:else}
									<button class="btn change-plan-button" on:click={() => changePlan(plan.priceId)}>
										Change Plan
									</button>
								{/if}
							{:else}
								<!-- User is logged in but has no active subscription -->
								<button class="btn subscribe-button" on:click={() => subscribe(plan.priceId)}>
									Subscribe
								</button>
							{/if}
						{:else}
							<!-- User is not logged in -->
							<a href="/login" class="btn">Log In</a>
						{/if}
					</div>
				{/if}
			{/each}
		</div>

		{#if showCouponInput}
			<div class="coupon-popup">
				<div>
					<h3>Got a Coupon Code?</h3>
					<input type="text" bind:value={couponCode} placeholder="Enter your coupon code" />
					<button class="btn" on:click={() => (showCouponInput = false)}>Submit</button>
					<button class="no-btn" on:click={() => (showCouponInput = false)}>Close</button>
				</div>
			</div>
		{/if}

		{#if subscription !== null}
			<div class="manage-row flex text-center">
				<a href="/subscribe/manage" class="btn manage-button">Manage your Plan</a>
			</div>
		{/if}
	</div>
{/if}

<style lang="scss">
	:root {
		--primary-color: rgb(254, 17, 0); /* Red */
		--secondary-color: #222222; /* Black */
		--light-color: #ffffff; /* White */
		--gray-color: #f5f5f5;
		--border-color: #ddd;
	}

	.container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 20px;
	}

	h1 {
		color: #222222;
		font-size: 2.5rem;
		margin-bottom: 2rem;
	}

	.plans {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		gap: 1.5rem;
	}

	.plan {
		background-color: var(--light-color);
		border: 1px solid var(--border-color);
		border-radius: 10px;
		padding: 1.5rem;
		flex: 1 1 calc(33.333% - 1rem);
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
		display: flex;
		flex-direction: column;
		align-items: center;
		max-width: 360px;
		position: relative;
	}

	.plan h2 {
		color: #222222;
		font-size: 1.6rem;
		margin-bottom: 1rem;
		margin-top: 0;
	}

	.plan p {
		color: #222222;
		font-size: 1rem;
		margin: 0.5rem 0;
	}

	.features {
		text-align: left;
		width: 100%;
		margin-bottom: 1.5rem;
	}

	.features ul {
		list-style: none;
		padding-left: 0;
		color: #222222;
	}

	.features li {
		margin-bottom: 0.5rem;
		position: relative;
		padding-left: 1.5rem;
	}

	.features li::before {
		content: '✓';
		position: absolute;
		left: 0;
		color: rgb(254, 17, 0);
		font-weight: bold;
	}

	button {
		background-color: rgb(254, 17, 0);
		color: var(--light-color);
		padding: 10px 20px;
		max-width: 280px;
		margin: auto;
		border: none;
		border-radius: 5px;
		cursor: pointer;
		font-size: 1.1rem;
		width: 100%;
		margin-top: auto;
	}

	.plan-details {
		margin-bottom: 1rem;
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
	}

	.plan-details strong {
		color: rgb(254, 17, 0);
	}

	.sub-prize-hero {
		background-color: rgb(229, 229, 229);
		border: 1px solid gold;
		text-align: center;
		padding: 8px 14px;
		max-width: 360px;
		margin: 0 auto;
		width: 100%;
		margin-left: 30px;

		.card {
			text-align: left;
			width: 100%;
			.flex {
				width: 100%;
				flex-direction: row;
				justify-content: space-between;
			}
			h5 {
				font-size: 0.9rem;
				margin: 0;
				width: 60%;
				font-weight: 600;
			}
			p {
				width: 30%;
				font-size: 0.9rem;
				text-align: center;
			}
		}

		&.active-sub {
			background-color: gold;
			> p {
				color: black;
				font-size: 1rem;
				text-decoration: underline;
			}
			.card p {
				font-weight: 600;
			}
		}
	}

	@media (max-width: 768px) {
		.plan {
			flex: 1 1 calc(50% - 1rem);
		}
		.sub-intro {
			flex-direction: column !important;
		}
		.sub-prize-hero {
			margin-left: 0;
		}
	}

	@media (max-width: 480px) {
		.plan {
			flex: 1 1 100%;
		}
	}

	.manage-button {
		background-color: #4caf50; /* a green-ish color */
	}
	.subscribe-button {
		background-color: rgb(254, 17, 0); /* red */
	}
	.change-plan-button {
		background-color: #ffa500; /* orange */
	}

	.manage-row {
		margin: 30px 0;
		a.btn {
			max-width: 280px;
			margin: auto;
		}
	}

	.coupon-btn {
		appearance: none;
		background-color: transparent;
		border: none;
		color: #222222;
		font-size: 0.9rem;
	}

	.coupon-popup {
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: 99;
		background-color: rgba(255, 255, 255, 0.9);
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: column;

		div {
			max-width: 490px;
			width: 100%;
			display: flex;
			justify-content: center;
			align-items: center;
			flex-direction: column;
		}

		input {
			margin-bottom: 30px;
			max-width: 280px;
			width: 100%;
			border: 1px solid #ccc;
			font-size: 1.1em;
			padding: 0.7rem 1rem;
			margin-bottom: 1.5em;
			border-radius: 4px;
			transition: ease all 0.2s;
		}
	}

	button.no-btn {
		appearance: none;
		background-color: transparent;
		border: none;
		color: #222222;
	}

	.coupon-active {
		justify-content: flex-start;
		align-items: center;

		button {
			margin-left: 10px;
			text-decoration: underline;
			text-align: left;
		}
	}
</style>
